import {
  DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD,
  FrequencyCap,
  FrequencyCapMode,
  triggerShowsConditionSummary,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { getTriggerDisplayName } from "@redotech/redo-flows-common/triggers";
import {
  WorkOrderFlowTriggerConditions,
  WorkOrderTriggerSource,
} from "@redotech/redo-fulfillment-operations-common/work-order-flow/work-order-flow";
import { useTranslation } from "@redotech/redo-i18n";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import TagIcon from "@redotech/redo-icons/line-chart-down-01_filled.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import ToolIcon from "@redotech/redo-icons/tool-02.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import UserPlusIcon from "@redotech/redo-icons/user-plus-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { calculateSelectionWrapperStyles } from "@redotech/redo-web/selection-wrapper-styles";
import { Text } from "@redotech/redo-web/text";
import { assertNever, DeepPartial } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import classNames from "classnames";
import { useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface TriggerNodeData {
  step: PartialWithRequiredType<TriggerStep>;
  triggerConditions?: WorkOrderFlowTriggerConditions;
  triggerSource?: WorkOrderTriggerSource;
  triggerBomIds?: string[];
  flowName?: string;
}

export function getTriggerIconForSchemaType(
  schemaType: SchemaType | undefined,
): (props: any) => React.ReactElement {
  switch (schemaType) {
    case SchemaType.CHECKOUT_TREE:
    case SchemaType.RCO_CHECKOUT_PACKAGE:
    case SchemaType.POST_PURCHASE_FLOW:
      return ShoppingCartIcon;
    case undefined:
    case SchemaType.RECOGNIZE:
    case SchemaType.EMAIL_MARKETING_SIGNUP:
    case SchemaType.MARKETING_BACK_IN_STOCK:
    case SchemaType.MARKETING_LOW_INVENTORY:
    case SchemaType.MARKETING_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_CAMPAIGN:
    case SchemaType.MARKETING_CHECKOUT_ABANDONMENT:
    case SchemaType.MARKETING_CART_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT:
    case SchemaType.MARKETING_BIGCOMMERCE_CART_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT:
    case SchemaType.MARKETING_BIGCOMMERCE_CHECKOUT_ABANDONMENT:
    case SchemaType.MARKETING_EMAIL:
    case SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE:
    case SchemaType.MARKETING_WARRANTY_REGISTRATION:
    case SchemaType.PRODUCT_SUBSCRIPTION_UPCOMING_CHARGE:
    case SchemaType.REVIEWS:
    case SchemaType.JUDGEME_REVIEW_SUBMITTED:
    case SchemaType.RETURN_TRACKING:
    case SchemaType.ORDER_TRACKING:
    case SchemaType.COVERAGE_CREDIT_EXPIRING:
    case SchemaType.HSA_ORDER:
    case SchemaType.HSA_QUESTIONNAIRE:
    case SchemaType.YOTPO_LOYALTY_EXPIRATION_REMINDER:
    case SchemaType.YOTPO_LOYALTY_POINTS_REMINDER:
    case SchemaType.YOTPO_LOYALTY_REDEMPTION_REMINDER:
    case SchemaType.YOTPO_LOYALTY_REDEMPTION_CREATED:
    case SchemaType.YOTPO_LOYALTY_REFERRAL_COMPLETED:
    case SchemaType.YOTPO_LOYALTY_REFERRAL_SHARED:
    case SchemaType.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY:
    case SchemaType.YOTPO_LOYALTY_OPT_IN:
    case SchemaType.YOTPO_LOYALTY_TIER_EARNED:
    case SchemaType.YOTPO_LOYALTY_TIER_LOST:
    case SchemaType.YOTPO_LOYALTY_POINTS_EARNED:
    case SchemaType.YOTPO_REVIEW_CREATED:
    case SchemaType.LOYALTY_LION_ENROLLED:
    case SchemaType.LOYALTY_LION_REWARD_AVAILABLE:
    case SchemaType.LOYALTY_LION_TIER_UPGRADE:
    case SchemaType.LOYALTY_LION_APPROACHING_TIER_UPGRADE:
    case SchemaType.LOYALTY_LION_REFERRAL_COMPLETE:
    case SchemaType.LOYALTY_LION_RULE_COMPLETED:
    case SchemaType.CUSTOM_EVENT:
    case SchemaType.SMS_MARKETING_SIGNUP:
    case SchemaType.OMS_NEW_FULFILLMENT_GROUP:
    case SchemaType.OMS_ORDER_READY_FOR_PICKUP:
    case SchemaType.OMS_LABEL_PURCHASED:
    case SchemaType.OMS_FULFILLMENT_LABEL_PURCHASED:
    case SchemaType.OMS_PACKING_SLIP_PRINTED:
    case SchemaType.ADDRESS_VERIFICATION_FAILED:
    case SchemaType.TNPL_TRACKING:
    case SchemaType.EXTENDED_WARRANTY:
    case SchemaType.TRACKING_PAGE_FLOW:
    case SchemaType.RULES:
    case SchemaType.RULES_V3:
    case SchemaType.SUPPORT_CSAT_SURVEY:
    case SchemaType.REFUND_RETURN_SUBMITTED:
    case SchemaType.EXCHANGE_PROCESSED_WITH_CREDIT:
    case SchemaType.MARKETING_AGENT:
    case SchemaType.RETURN_PROCESSING_FLOW:
    case SchemaType.COMPENSATION_FLOW:
    case SchemaType.RESTOCKING_FLOW:
    case SchemaType.WITHDRAWAL_FLOW:
    case SchemaType.MARKETING_PRODUCT_SUBSCRIPTION_CANCELLED:
    case SchemaType.MARKETING_GIFT_CARD_BALANCE:
      return UserPlusIcon;
    case SchemaType.MARKETING_DATE:
      return CalendarIcon;
    case SchemaType.MARKETING_PRICE_DROP:
      return TagIcon;
    case SchemaType.WORK_ORDER:
      return ToolIcon;
    case SchemaType.SUPPLIES_SHIPPING:
      return TruckIcon;
    default:
      assertNever(schemaType);
  }
}

function getStyledTriggerIconForSchemaType(schemaType: SchemaType | undefined) {
  const icon = getTriggerIconForSchemaType(schemaType);
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (schemaType) {
    case SchemaType.CHECKOUT_TREE:
    case SchemaType.RCO_CHECKOUT_PACKAGE:
    case SchemaType.POST_PURCHASE_FLOW:
    case SchemaType.MARKETING_CART_ABANDONMENT:
    case SchemaType.MARKETING_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_CHECKOUT_ABANDONMENT:
      return (
        <RedoFeaturedIcon
          color="black"
          Icon={icon}
          size="xs"
          type="high-contrast"
        />
      );
    default:
      return <RedoFeaturedIcon color="black" Icon={icon} size="xs" />;
  }
}

export function TriggerNode(data: {
  id: string;
  data: TriggerNodeData;
  selected?: boolean | undefined;
}): React.ReactNode {
  const { t } = useTranslation();
  const featuredIcon = useMemo(() => {
    return getStyledTriggerIconForSchemaType(data.data.step.schemaType);
  }, [data.data.step.schemaType]);

  const isWorkOrder = data.data.step.schemaType === SchemaType.WORK_ORDER;
  const frequencyCapSummary = (
    frequencyCap: DeepPartial<FrequencyCap> | null | undefined,
  ): string => {
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (frequencyCap?.mode) {
      case FrequencyCapMode.NO_REENTRY:
        return t("No re-entry");
      case FrequencyCapMode.COOLDOWN:
        return frequencyCap.value && frequencyCap.unit
          ? `${frequencyCap.value} ${frequencyCap.unit.toLowerCase()}`
          : t("Not set");
      default:
        return t("No limit");
    }
  };

  const title = isWorkOrder
    ? (data.data.flowName ?? t("Work order flow"))
    : (getTriggerDisplayName(data.data.step.key) ?? "");

  if (isWorkOrder) {
    const source = data.data.triggerSource ?? WorkOrderTriggerSource.PRODUCT;
    const subtext =
      source === WorkOrderTriggerSource.BOM
        ? (() => {
            const bomCount = data.data.triggerBomIds?.length ?? 0;
            return bomCount > 0
              ? t("{{count}} BOMs selected", { count: bomCount })
              : t("No BOMs selected");
          })()
        : (() => {
            const countLeaves = (children: any[]): number => {
              return children.reduce((sum, child) => {
                if (child.nodeType === "leaf") {
                  return sum + 1;
                }
                return sum + countLeaves(child.children || []);
              }, 0);
            };
            const count = countLeaves(
              data.data.triggerConditions?.children ?? [],
            );
            return count > 0
              ? t("{{count}} product conditions", { count })
              : t("No conditions configured");
          })();

    const { wrapperStyle } = calculateSelectionWrapperStyles({
      selected: data.selected,
    });

    return (
      <>
        <Flex
          className={classNames(
            NodeCSS.nodeWrapper,
            data.selected ? NodeCSS.nodeWrapperSelected : undefined,
          )}
          dir="row"
          gap="none"
          style={wrapperStyle}
        >
          <Flex className={NodeCSS.analytics} dir="column" gap="sm" mt="none">
            <Text fontSize="sm" fontWeight="semibold" textColor="white">
              {title}
            </Text>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="semibold">
                {t("Gather all BOM inputs")}
              </Text>
              <Text
                className={NodeCSS["secondary-metric"] as string}
                fontSize="xs"
              >
                {subtext}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Handle position={Position.Bottom} type="source" />
      </>
    );
  }

  return (
    <>
      <BaseNode
        icon={featuredIcon}
        selected={data.selected}
        step={data.data.step}
        title={title}
      />

      {triggerShowsConditionSummary(data.data.step) && (
        <Flex className={NodeCSS.analytics}>
          <Flex dir="column" gap="xs" w="full">
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">{t("Entry conditions")}</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {data.data.step.entryConditions?.conditions?.length ?? "-"}
              </Text>
            </Flex>
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">{t("Frequency cap")}</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {frequencyCapSummary(data.data.step.frequencyCap)}
              </Text>
            </Flex>
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">{t("Skip conditions")}</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {data.data.step.skipConditions?.conditions?.length ?? "-"}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}

      {data.data.step.schemaType === SchemaType.CUSTOM_EVENT && (
        <Flex className={NodeCSS.analytics}>
          <Flex dir="column" gap="xs" w="full">
            {"eventName" in data.data.step && (
              <Flex justify="space-between" w="full">
                <Text fontSize="xs">{t("Event")}</Text>
                <Text fontSize="xs" fontWeight="semibold">
                  {(data.data.step.eventName as string | undefined) ?? "-"}
                </Text>
              </Flex>
            )}
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">{t("Property conditions")}</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {("triggerSpecificFields" in data.data.step
                  ? (
                      data.data.step.triggerSpecificFields as
                        | { conditions?: unknown[] }
                        | null
                        | undefined
                    )?.conditions?.length
                  : undefined) ?? 0}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}

      {data.data.step.schemaType === SchemaType.MARKETING_GIFT_CARD_BALANCE && (
        <Flex className={NodeCSS.analytics}>
          <Flex justify="space-between" w="full">
            <Text fontSize="xs">{t("Days held")}</Text>
            <Text fontSize="xs" fontWeight="semibold">
              {("triggerSpecificFields" in data.data.step
                ? (
                    data.data.step.triggerSpecificFields as
                      | { minimumDaysHeld?: number }
                      | null
                      | undefined
                  )?.minimumDaysHeld
                : undefined) ?? DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD}
            </Text>
          </Flex>
        </Flex>
      )}

      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
